import type { RefObject } from "react";
import { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { Channel } from "@hybrid-iptv/types";
import { ChannelRow, CHANNEL_ROW_HEIGHT } from "./ChannelRow.js";
import type { ChannelProgramIndex } from "./hooks.js";
import { EPG_TIMELINE_HEADER_HEIGHT } from "./EpgScrollRail.js";

interface ChannelListProps {
  channels: Channel[];
  selectedChannelId: string | null;
  programIndex: ChannelProgramIndex | null;
  onSelect: (channel: Channel) => void;
  /** Hold / right-click on a channel row: the quick-action menu. */
  onContext?: (channel: Channel, point: { x: number; y: number }) => void;
  virtualScrollRef: RefObject<HTMLDivElement | null>;
  /** Current D-pad focus position (for the roving ring + tabindex). */
  focusRow?: number;
  focusCol?: number;
  /** Called when a row is clicked, so the D-pad ring follows the mouse. */
  onFocusAt?: (row: number, col: number) => void;
}

/** Virtualized channel viewport driven by the guide's shared scroll rail. */
export function ChannelList({
  channels,
  selectedChannelId,
  programIndex,
  onSelect,
  onContext,
  virtualScrollRef,
  focusRow = 0,
  focusCol = -1,
  onFocusAt,
}: ChannelListProps) {
  const localScrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: channels.length,
    getScrollElement: () => virtualScrollRef.current ?? localScrollRef.current,
    estimateSize: () => CHANNEL_ROW_HEIGHT,
    overscan: 8,
  });

  if (channels.length === 0) {
    return (
      <div className="epg-channel-list epg-channel-list--empty">
        <span className="epg-empty-text">No channels in this category</span>
      </div>
    );
  }

  const scrollOffset = virtualizer.scrollOffset ?? 0;
  return (
    <div className="epg-channel-list">
      <div
        className="epg-channel-list__content"
        style={{ height: channels.length * CHANNEL_ROW_HEIGHT + EPG_TIMELINE_HEADER_HEIGHT }}
      >
        {virtualizer.getVirtualItems().map((item) => {
          const channel = channels[item.index]!;
          return (
            <div
              key={item.key}
              className="epg-channel-list__virtual-row"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: item.size,
                transform: `translateY(${EPG_TIMELINE_HEADER_HEIGHT + item.start - scrollOffset}px)`,
              }}
            >
              <ChannelRow
                channel={channel}
                nowPlaying={programIndex?.getNowPlaying(channel.id) ?? null}
                isSelected={channel.id === selectedChannelId}
                hasEpgData={programIndex?.hasData(channel.id) ?? false}
                guideRow={item.index}
                isFocused={focusRow === item.index && focusCol === -1}
                inFocusedRow={focusRow === item.index}
                onClick={() => {
                  onFocusAt?.(item.index, -1);
                  onSelect(channel);
                }}
                onContextAction={
                  onContext
                    ? (point) => {
                        onFocusAt?.(item.index, -1);
                        onContext(channel, point);
                      }
                    : undefined
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { CHANNEL_ROW_HEIGHT };
